import {
  DefaultError,
  InfiniteData,
  keepPreviousData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

export type EntityGroup = Endpoints['GET /entity-groups']['response']['entityGroups'][number];

export type EntityGroupDetail =
  Endpoints['GET /entity-groups/:entityGroupId']['response']['entityGroup'];

export const useEntityGroup = (entityGroupId: string | undefined) => {
  const fetch = useFetch<Endpoints['GET /entity-groups/:entityGroupId']>();

  return useQuery({
    queryKey: ['entity-group', entityGroupId],
    queryFn: () => fetch(`/entity-groups/${entityGroupId!}`),
    enabled: !!entityGroupId,
  });
};

export const useEntityGroups = (
  params?: Endpoints['GET /entity-groups']['query'],
  options: {
    keepPreviousData?: boolean;
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /entity-groups']>();

  return useQuery({
    queryKey: ['entity-groups', params],
    queryFn: () => fetch('/entity-groups', params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useInfiniteEntityGroups = (params?: Endpoints['GET /entity-groups']['query']) => {
  const fetch = useFetch<Endpoints['GET /entity-groups']>();
  const limit = params?.limit ?? 20;

  return useInfiniteQuery<
    Endpoints['GET /entity-groups']['response'],
    DefaultError,
    InfiniteData<Endpoints['GET /entity-groups']['response']>,
    QueryKey,
    number
  >({
    queryFn: ({ pageParam }) => fetch(`/entity-groups`, { ...params, offset: pageParam }),
    queryKey: ['entity-groups', 'infinite', params],
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.count < limit) {
        return undefined;
      } else {
        return lastPage.meta.offset + limit;
      }
    },
  });
};

export const useCreateEntityGroup = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /entity-groups']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /entity-groups']['body']) =>
      fetch('/entity-groups', undefined, {
        method: 'POST',
        body,
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entity-groups'] });
    },
  });
};

export const useUpdateEntityGroup = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /entity-groups/:entityGroupId']>();

  return useMutation({
    mutationFn: ({
      entityGroupId,
      body,
    }: {
      entityGroupId: string;
      body: Endpoints['PATCH /entity-groups/:entityGroupId']['body'];
    }) =>
      fetch(`/entity-groups/${entityGroupId}`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entity-groups'] });
      if (variables?.entityGroupId) {
        queryClient.invalidateQueries({ queryKey: ['entity-group', variables.entityGroupId] });
      }
    },
  });
};

export const useDeleteEntityGroup = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /entity-groups/:entityGroupId']>();

  return useMutation({
    mutationFn: (entityGroupId: string) =>
      fetch(`/entity-groups/${entityGroupId}`, undefined, {
        method: 'DELETE',
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entity-groups'] });
    },
  });
};
